import { environment } from '@blueberry/framework/config'
import * as Sentry from '@sentry/browser'

export type SentryConfig = {
  dsn: string
}

/**
 * Get Sentry configuration based on the current environment.
 * - In 'local' environment, Sentry is disabled.
 * - In 'staging' environment, Sentry is disabled.
 * - In 'production' environment, Sentry is enabled with a lower sample rate for tracing.
 * - For any other environment, Sentry is disabled.
 * @returns Sentry.BrowserOptions - Configuration options for Sentry initialization.
 */
const getConfig = (): Sentry.BrowserOptions => {
  switch (environment) {
    case 'local': {
      return {
        enabled: false, // Disable Sentry in local development
      }
    }
    case 'staging':
      return {
        enabled: false,
      }
    case 'production':
      return {
        tracesSampleRate: 0.1,
        replaysOnErrorSampleRate: 0,
        replaysSessionSampleRate: 0,
        enableLogs: true,
        integrations: [
          Sentry.consoleLoggingIntegration({
            levels: ['info', 'warn', 'error'],
          }),
        ],
      }
    default:
      return {
        enabled: false, // Disable Sentry for any other environment
      }
  }
}

export const initializeSentry = (config: SentryConfig) => {
  Sentry.init({
    ...getConfig(),
    dsn: config.dsn,
    environment: environment,
    ignoreErrors: [
      // Turbo aborts in-flight fetches when the user navigates away. We don't use the AbortController in our codebase.
      /AbortError:/,
      // iOS Safari's equivalent of a failed fetch — fires when Turbo frame fetches fail due to network drops, backgrounded tabs, or iOS killing connections.
      /TypeError: Load failed/,
    ],
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', 'local.blueberrypediatrics.dev'],
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
  })
}
